import { MagnetDetailsEntry } from './CalibrationParameters';

// ---- Intent/actual split records (per-run sensor columns) ----
// The claim vs the observation: `sensors_intent` holds only what the user
// asserted (ids, primary, wheel positions, magnet counts), `sensors_actual`
// only what the device observed (id -> logging instance, transport). See
// .claude-plans/sensors-intent-actual-split.md.

export interface SensorIntentEntry {
  hash_id: number;
  // SensorFunctionsEnum member name ('SpeedSensor', 'TyrePressure', ...).
  function?: string | null;
  primary?: boolean;
  // PositionEnum ordinal (same vocabulary as SpeedSensorEntry.position).
  position?: number;
  magnet_count?: number | null;
}

export interface SensorsIntent {
  gps_speed?: boolean;
  sensors?: SensorIntentEntry[];
}

export interface SensorActualEntry {
  // Bundle kind: 'sensor' (default) or a phone source ('phone_gps', ...).
  source?: string;
  hash_id?: number | null;
  function?: string | null;
  // The i{n} logging instance this sensor's pages carried; gaps ({0, 2})
  // are normal — never assume contiguity.
  run_instance?: number | null;
  transport?: 'ant' | 'ble' | string | null;
  fw_version?: string | null;
}

export interface SensorsActual {
  sensors?: SensorActualEntry[];
}

// The run's magnet-spacing fit ledger: id-keyed
// ({str(ant_device_number): fit}) going forward; historical rows hold a
// bare fit or an i{n}-keyed dict.
export type RunMagnetCalc = Record<string, MagnetDetailsEntry> | MagnetDetailsEntry;

export interface VelodromeAxis {
  major_axis_bearing_deg: number;
  minor_axis_bearing_deg: number;
  confidence: number;
  n_samples_used: number;
}

export interface LapMarkers {
  edited?: boolean | null;
  idx_end?: number[] | null;
  idx_start?: number[] | null;
  inactive_laps: boolean[] | null; // USER (manual) exclusions
  inactive_laps_auto?: boolean[] | null; // CODE exclusions (e.g. incomplete traverses)
  markers_rel_ts?: number[] | null;
  start_ts?: number | null;
  velodrome_axis?: VelodromeAxis | null;
}

/**
 * Per-lap "excluded" flags combining both authors: a lap is inactive if the USER
 * excluded it (`inactive_laps`) OR the pipeline did (`inactive_laps_auto`, e.g. an
 * incomplete traverse). Use this for any display/aggregation that asks "is this lap
 * excluded?" — the editable manual state still reads `inactive_laps` directly.
 */
export function combinedInactiveLaps(
  lapMarkers?: { inactive_laps?: boolean[] | null; inactive_laps_auto?: boolean[] | null } | null,
): boolean[] {
  const user = lapMarkers?.inactive_laps ?? [];
  const auto = lapMarkers?.inactive_laps_auto ?? [];
  const n = Math.max(user.length, auto.length);
  const out: boolean[] = [];
  for (let i = 0; i < n; i += 1) {
    out.push(Boolean(user[i]) || Boolean(auto[i]));
  }
  return out;
}

// Run processing lifecycle (mirrors backend RunStatus IntEnum). Values in
// [3500, 4000) are a legacy "success with health issues" band still present
// on old rows.
export enum RunStatus {
  CREATED = 1000,
  UPLOADED_MATCHED = 2000,
  PROCESSING = 2200,
  PROCESSED_ERROR = 3000,
  PROCESSED_SUCCESS = 4000,
}

// Health status enum values (mirrors backend RunHealth IntEnum)
export enum RunHealthStatus {
  GOOD = 0,
  NONE = 1,
  AVERAGE = 50,
  WARNING = 100,
  ERROR = 500,
}

// run_status bands: < 3000 still in flight (CREATED / UPLOADED_MATCHED /
// PROCESSING), [3000, 3500) pipeline failure (PROCESSED_ERROR), >= 3500
// processed (the legacy 4000 - health encoding lands here too).
export const RUN_STATUS_FAILED = RunStatus.PROCESSED_ERROR;
export const RUN_STATUS_PROCESSED = 3500;

// An unknown status is treated as settled, not settling: payload scopes that
// omit run_status must not make a run look perpetually in flight (and must
// never start a poll that can never terminate).
export const isRunSettling = (status?: number | null): boolean => status != null && status < RUN_STATUS_FAILED;

export const isRunFailed = (status?: number | null): boolean =>
  status != null && status >= RUN_STATUS_FAILED && status < RUN_STATUS_PROCESSED;

export const isRunProcessed = (status?: number | null): boolean => status != null && status >= RUN_STATUS_PROCESSED;

// The subset of the settling band that is genuinely progressing towards a
// terminal state, and is therefore worth polling. CREATED is excluded on
// purpose: a planned (bare-created) run sits there indefinitely by design, so
// polling it would never stop. Uploaded/processing runs always resolve — to
// PROCESSED_ERROR at worst — or trip the caller's watchdog.
export const isRunActivelySettling = (status?: number | null): boolean =>
  status === RunStatus.UPLOADED_MATCHED || status === RunStatus.PROCESSING;

export enum ProcessingStage {
  INGESTION = 1500,
  METADATA = 2000,
  SENSORS = 3000,
  SESSION_SPECIFIC_PRE_GRID = 4000,
  ROUTE_PRE_GRID = 5000,
  HEALTH_CHECK = 5500,
  GRID = 6000,
  ROUTE_POST_GRID = 6500,
  SESSION_SPECIFIC_POST_GRID = 7000,
  CDA_CALCULATION = 8000,
  POST_CDA_CALCULATION = 9000,
  FINALIZATION = 10000,
  COMPLETE = 100000,
}

export function getStageName(code: number | null | undefined): string {
  if (code == null) return 'Unknown';
  if (code >= 100000) return 'Complete';
  if (code >= 10000) return 'Finalization';
  if (code >= 9000) return 'Post-CdA';
  if (code >= 8000) return 'CdA Calculation';
  if (code >= 7000) return 'Post-Grid Processing';
  if (code >= 6500) return 'Route Post-Grid';
  if (code >= 6000) return 'Grid';
  if (code >= 5500) return 'Health Check';
  if (code >= 5000) return 'Route Pre-Grid';
  if (code >= 4000) return 'Pre-Grid Processing';
  if (code >= 3000) return 'Sensors';
  if (code >= 2000) return 'Metadata';
  if (code >= 1500) return 'Ingestion';
  return 'Not Started';
}

// Timeseries summary used as both raw (L00) and gridded (G10/G15) views.
// Timing fields stay null on the gridded view.
export interface SensorChannelStats {
  n_samples: number;
  duration_s: number | null;
  sample_rate_hz: number | null;
  dt_std_s: number | null;
  min: Record<string, number>;
  max: Record<string, number>;
  mean: Record<string, number>;
}

// Per-sensor presence + raw/gridded value distribution. Subclassed in
// the server for sensors carrying channel-quality extras (GPS accuracy,
// ANT packet loss).
export interface SensorHealthBase {
  present: boolean;
  raw: SensorChannelStats;
  gridded: SensorChannelStats;
  // Derived server-side: true iff the sensor logged samples on the raw OR
  // gridded view. Gates the "Good"/"Missing" badge instead of the looser
  // `present` flag (which only records the channel/file existed). Optional —
  // older payloads omit it, so callers fall back to the sample counts.
  has_data?: boolean;
}

export interface AntSensorHealth extends SensorHealthBase {
  sensor_key: string;
  packet_count_sequential: boolean;
  packets_dropped: number;
  packets_dropped_fraction: number | null;
  packets_collision: number;
  // cadence_source moved to solve.selections.cadence_source
}

export interface GpsHealth extends SensorHealthBase {
  accuracy_p90: number | null;
  // Median of GpsAccuracy_sL00 (m). Absent on payloads processed before it
  // was added — consumers fall back to raw.mean.accuracy.
  accuracy_median?: number | null;
  // Fix-rate-while-moving diagnostics; moving mask from wheel speed
  // ('speed') or GPS speed fallback ('gps'). Absent on older payloads.
  moving_source?: string | null;
  moving_duration_s?: number | null;
  moving_fix_count?: number | null;
  moving_fix_hz?: number | null;
  pct_under_4m: number | null;
  pct_4_to_8m: number | null;
  pct_8_to_20m: number | null;
  pct_over_20m: number | null;
}

// IMU (accel/gyro/mag) — bare base; signal-rate stability is captured
// by raw.dt_std_s, no extra fields needed.
export type ImuSensorHealth = SensorHealthBase;

export interface GateHealth {
  present: boolean;
  gate_a_lat: number | null;
  gate_a_lon: number | null;
  gate_a_beta: number | null;
  gate_b_lat: number | null;
  gate_b_lon: number | null;
  gate_b_beta: number | null;
  gates_lat: number[] | null;
  gates_lon: number[] | null;
  bearing_deg: number | null;
  route_and_gates_intersect?: boolean | null;
  gates_defined_count?: number;
  gates_crossed_count?: number;
}

export interface RouteHealth {
  present: boolean;
  start_lat: number | null;
  start_lon: number | null;
  end_lat: number | null;
  end_lon: number | null;
  route_length_m: number | null;
  segment_count: number | null;
  bearing_deg: number | null;
}

export interface GateIntersection {
  route_and_gates_intersect: boolean | null;
  gates_defined_count: number;
  gates_crossed_count: number;
  // fallback_level moved to solve.selections.route_fallback_level
}

// --- Solve object types ---

export type GateStatus = 'UNUSED' | 'START' | 'SPLIT' | 'FINISH';

export interface SolveGate {
  lat: number;
  lon: number;
  pos?: number | null;
  bearing?: number | null;
  status: GateStatus;
}

export interface SolveRouteMetadata {
  route_origin: string;
  route_origin_init: string;
  gates_origin: string;
  gates_origin_init: string;
  route_valid: boolean;
  gates_valid: boolean;
  incorrect_db_data: boolean;
  route_type: string;
  route_category: number;
  fallback_used: boolean;
  fallback_detail?: string | null;
}

// Static inputs to the solver — snapshot of CalibrationConst plus DB-
// lookup route/gate identity. Replaces the legacy flat fields that
// used to sit directly on SolveData. Old v1 records are auto-lifted
// into this shape by the Python read-side validator.
export interface SolveInputs {
  combined_mass?: number | null;
  crr?: number | null;
  cp_corr?: number | null;
  yaw_offset?: number | null;
  wheel_circumference?: number | null;
  input_wheel_circumference?: number | null;
  power_meter_slope?: number | null;
  alt_power?: number | null;
  alt_spd?: number | null;
  alt_cad?: number | null;
  gps_speed?: boolean | null;
  head_slope?: number | null;
  head_ref?: number | null;
  cda_ref?: number | null;
  airspeed_ref?: number | null;
  calculation_version?: number | null;
  session_analysis_type?: number | null;
  front_tooth_count?: number | null;
  gate_id?: string | null;
  route_id?: string | null;
  gate_hash?: string | number | null;
  gates?: SolveGate[] | null;
  route_metadata?: SolveRouteMetadata | null;
}

export interface SpeedSelection {
  instance: number | null;
  // ANT device number (or BLE pseudo device number) of the selected
  // primary sensor — the authoritative "which sensor is the primary
  // series" for per-sensor labelling. Absent on GPS-only runs and runs
  // processed before id-based selection.
  ant_device_number?: number | null;
  page_family: string | null;
  source: string | null;
  gps_primary: boolean;
  gps_fallback: boolean;
}

export interface PowerMeterSelection {
  instance: number | null;
  page: number | null;
  source_label: string | null;
}

// Which crank-cadence source the solver used. `page_family` is 'bikepwr'
// (cadence rode on a power meter's pages) or 'spdcad' (a speed+cadence
// sensor) — the two carry different sensor functions, so it is what ties
// `instance` back to a sensor. `secondary_instance` is set only when a
// second source was found and emitted as the CrankAltCad_R channel set;
// it deliberately does not record its own page family.
export interface CadenceSelection {
  instance?: number | null;
  page_family?: string | null;
  page?: number | null;
  source_label?: string | null;
  secondary_instance?: number | null;
  unit?: string;
}

// Classification of where an atmospheric quantity came from. Ordinal is
// proximity/directness to the bike, NOT selection preference.
export enum AtmosphericSourceEnum {
  UNKNOWN = 0,
  CONSTANT_STD = 10,
  CONSTANT_MANUAL = 15,
  REMOTE = 20,
  LOCAL = 30,
  ON_BIKE_SYNTHETIC = 40,
  HYBRID = 45,
  ON_BIKE_INDIRECT = 50,
  ON_BIKE_DIRECT = 60,
}

// Short human labels for AtmosphericSourceEnum values (badges/tooltips).
export const ATMOSPHERIC_SOURCE_LABEL: Record<AtmosphericSourceEnum, string> = {
  [AtmosphericSourceEnum.UNKNOWN]: 'Unknown',
  [AtmosphericSourceEnum.CONSTANT_STD]: 'Std constant',
  [AtmosphericSourceEnum.CONSTANT_MANUAL]: 'Manual',
  [AtmosphericSourceEnum.REMOTE]: 'Weather API',
  [AtmosphericSourceEnum.LOCAL]: 'Local station',
  [AtmosphericSourceEnum.ON_BIKE_SYNTHETIC]: 'Modelled',
  [AtmosphericSourceEnum.HYBRID]: 'Probe+API',
  [AtmosphericSourceEnum.ON_BIKE_INDIRECT]: 'Session probe',
  [AtmosphericSourceEnum.ON_BIKE_DIRECT]: 'On-bike sensor',
};

export interface AirDensitySource {
  temperature: AtmosphericSourceEnum;
  barometric_pressure: AtmosphericSourceEnum;
  humidity: AtmosphericSourceEnum;
}

// synthetic false → probe DynPres × cp_corr; true → 0.5·ρ·v² from speed
// (speed source lives on SolveSelections.speed, not duplicated here).
export interface DynamicPressureSelection {
  synthetic: boolean;
  /** Measured DynPres was overridden via force_synthetic_dp (vs organic no-probe synthesis). */
  forced?: boolean;
  air_density: AirDensitySource;
  backfilled_channels: string[];
}

// Runtime branch decisions the solver made — what was chosen at each
// fallback point. Every field here describes a choice with at least
// one alternative; static inputs go in SolveInputs instead.
export interface SolveSelections {
  speed: SpeedSelection;
  power_meter: PowerMeterSelection;
  cadence?: CadenceSelection;
  cadence_source: string | null;
  velodrome_bend_signal: string | null;
  route_fallback_level: number | null;
  weather_sources: WeatherSourceSelection[];
  dynamic_pressure?: DynamicPressureSelection;
}

export interface SolveData {
  version: number;
  inputs: SolveInputs;
  selections: SolveSelections;
}

export interface BodySensorHealth extends AntSensorHealth {
  ref_chest_cm: number | null;
  ref_head_cm: number | null;
  ref_waist_cm: number | null;
}

export interface WindDiffRefs {
  diff_ref_1_pa: number | null;
  diff_ref_2_pa: number | null;
  diff_ref_3_pa: number | null;
}


export interface SensorInfoObj {
  ant_device_number: number | null;
  ant_id_not_found_reason: string | null;
  battery_voltage: number | null;
  firmware_version: string | null;
  // Which sensor of this function the run treated as primary, and where its
  // wheel sits. The key this object sits under is a device slot, which is NOT
  // the pipeline's primary/alt ordering, so the role is recorded rather than
  // read off the key. Null when the run's records never settled it; absent
  // entirely on runs processed before the fields existed (see sensorRoles.ts,
  // which re-derives both client-side for those).
  primary?: boolean | null;
  // SPEED_POSITION ordinal. Only speed and tyre-pressure sensors have one.
  position?: number | null;
}

// Tempo Multi magnet-spacing outcome — a compact echo of the run's
// magnet_calc fit plus which spacing the speed calculation actually applied.
// Absent on single-magnet / pre-field runs.
export interface MagnetSpacingHealth {
  source?: 'calibrated' | 'fitted' | 'uniform' | null;
  status?: string | null;
  quality?: number | null;
  max_gap_deg?: number | null;
  n_windows?: number | null;
  n_slips_healed?: number | null;
  // Event stamps moved by the L30 jerk restamp filter (bounced /
  // mis-taken magnet passes, typical at walking pace).
  n_jerk_restamped?: number | null;
  ant_device_number?: number | null;
  magnet_count?: number | null;
}

export interface SpeedHealthObj {
  wheel_skip_count: number | null;
  wheel_skip_ratio: number | null;
  gps_wheel_crc_in_tolerance: number;
  tolerance: number;
  magnet_spacing?: MagnetSpacingHealth | null;
}

export enum DiffSensorStatus {
  GOOD = 0,
  NEEDS_AUTOCAL = 1,
  ERROR = 2,
  UNSTABLE = 3,
  UNKNOWN = 4,
}

export interface DiffChannelHealth {
  start_median_pa: number | null;
  start_std_pa: number | null;
  end_median_pa: number | null;
  end_std_pa: number | null;
  reference_pa: number | null;
  status: number;
}

export interface ProbeHealth {
  mean_abs_dev: number | null;
  n_samples: number | null;
  status: number;
}

export interface WindSensorHealth {
  diff1: DiffChannelHealth;
  diff2: DiffChannelHealth;
  diff3: DiffChannelHealth;
  probe_health?: ProbeHealth;
}

// ── Lap parsing health diagnostics ──

export interface CatesbyLapDetection {
  lap_number: number;
  threshold_crossing_idx: number;
  stability_confirmed_idx: number;
  lap_start_idx: number;
  lap_finish_idx: number;
  threshold_crossing_time_s: number;
  stability_confirmed_time_s: number;
  lap_start_time_s: number;
  lap_finish_time_s: number;
  lap_duration_s: number;
  threshold_crossing_distance_m: number;
  lap_start_distance_m: number;
  lap_finish_distance_m: number;
  deceleration_alert: boolean;
  active: boolean;
}

export interface CatesbyRejectedCrossing {
  crossing_idx: number;
  crossing_time_s: number;
  crossing_distance_m: number;
  target_start_idx?: number | null;
  target_start_distance_m?: number | null;
  reason: string;
}

export interface VelodromeLapDetection {
  lap_number: number;
  lap_start_idx: number;
  lap_finish_idx: number;
  lap_start_time_s: number;
  lap_finish_time_s: number;
  lap_duration_s: number;
  lap_start_distance_m: number;
  lap_finish_distance_m: number;
  active: boolean;
}

export interface LapParsingHealth {
  parser_type: string;
  laps_detected: number;
  laps_active: number;
  // Velodrome-specific fields (present when parser_type === 'velodrome')
  correction_applied?: boolean;
  correction_failed?: boolean;
  correction_error?: string | null;
  // Catesby-specific fields (present when parser_type === 'catesby')
  speed_threshold_revs?: number;
  stability_window_s?: number;
  distance_offset_m?: number;
  first_traverse_offset_m?: number;
  lap_length_m?: number;
  wheel_circumference_m?: number;
  data_interval_s?: number;
  speed_80th_percentile_revs?: number | null;
  total_crossings_found?: number;
  total_distance_m?: number | null;
  total_duration_s?: number | null;
  deceleration_warning?: boolean;
  laps_with_deceleration?: number;
  // Per-lap detection records; shape depends on parser_type. Narrow with
  // an `as CatesbyLapDetection[]` / `as VelodromeLapDetection[]` cast.
  laps?: (CatesbyLapDetection | VelodromeLapDetection)[];
  rejected_crossings?: CatesbyRejectedCrossing[];
}

export interface WhlRouteLapResult {
  n_valid: number;
  slope: number;
  whlcrc: number;
  kept: boolean;
  removed_by?: string | null; // 'length' | 'tolerance' | 'whlcrc'
}

export interface WhlRouteMappingHealth {
  cardinal_laps_found: number;
  initial_results: number;
  results: WhlRouteLapResult[];
  final_results: number;
  min_route_length_n: number;
  whlcrc_expected: number;
  whlcrc_deviation?: number | null;
  circumference_calculated?: number | null;
  l20_valid_fraction?: number | null;
}

export interface TraverseGateFinderHealth {
  gate_low: number;
  gate_high: number;
  n_grid_points: number;
  n_valid_points: number;
  n_nan_points: number;
  first_valid_idx?: number | null;
  traverses_detected: number;
  gate_crossings: number;
  direction_changes: number;
  aborted_traverses: number;
}

export interface CdaOutputHealth {
  cda_present: boolean;
  cda_normalised_present: boolean;
  n_traverses: number;
  n_cda_finite: number;
  n_cda_normalised_finite: number;
}

// Outcome of the crosswind/yaw-offset (Cp) bearing-split analysis. When the
// direction split collapses (one direction with zero laps within the cone),
// the yaw offset comes out non-finite; the run still produces a normalised
// CdA from the uncorrected yaw, but `valid` is false. Widen
// `parser_vars.traverse.cp_bearing_cone` to recover the correction.
export interface CpAnalysisHealth {
  attempted: boolean;
  valid: boolean;
  out_lap_count: number;
  in_lap_count: number;
  bearing_cone_deg?: number | null;
  cp_adj?: number | null;
  yaw_offset_deg?: number | null;
}

// End-of-pipeline summary of a traverse test's structure: 1 start, 2N
// traverses, 2N-1 turnarounds, 1 finish, where N = laps. `symmetric` is
// directional balance (out_traverses === in_traverses, all classified).
// `structure_ok` is the all-clear; when false the card shows a warning.
export interface TraverseStructureHealth {
  traverses: number;
  laps: number;
  out_traverses: number;
  in_traverses: number;
  symmetric: boolean;
  turnarounds: number;
  expected_turnarounds: number;
  start_count: number;
  finish_count: number;
  incomplete_traverses: number;
  structure_ok: boolean;
}

export interface RunHealthObj {
  sensors_present: {
    power_meter: AntSensorHealth;
    wind: AntSensorHealth;
    aero?: AntSensorHealth; // legacy key, use wind instead
    speed: AntSensorHealth;
    body?: BodySensorHealth;
    power_meter_2?: AntSensorHealth;
    power_meter_3?: AntSensorHealth;
    speed_2?: AntSensorHealth;
    speed_3?: AntSensorHealth;
    gps: GpsHealth;
    accel?: ImuSensorHealth;
    gyro?: ImuSensorHealth;
    mag?: ImuSensorHealth;
    gates: GateHealth;
    gates_run_original?: GateHealth;
    route: RouteHealth;
  };
  missing_sensors: string[];
  missing_optional_sensors?: string[];
  wind_diff_refs?: WindDiffRefs;
  sensor_info?: Record<string, SensorInfoObj>;
  gate_intersection?: GateIntersection;
  speed_health: SpeedHealthObj;
  wind_sensor_health?: WindSensorHealth;
  health_status: number;
  health_status_name: string;
  health_status_reasons?: string[];
  // Warnings relaxed away by a per-session session_health_vars threshold
  // override — reported (observable) but not escalating the run. Omitted
  // server-side when empty, so clean/older runs simply don't carry it.
  snoozed_reasons?: string[] | null;
  health_code?: number;
  total_run_time_seconds?: number;
  lap_parsing?: LapParsingHealth | null;
  // The merged (session ← run) parser_vars this run was processed with.
  // Diff it against the current settings to tell that stored results predate
  // an edit. Three states: absent = processed before this was recorded (so
  // unknowable, don't claim stale); {} = processed with no overrides;
  // populated = the overrides in force at processing.
  parser_vars_used?: ParserVars | null;
  weather?: WeatherHealth | null;
  // High-rate source detection outcome ({source: median_hz} over
  // aero5_raw / imu_accel / wheel_per_rev), recorded by the HTS grid gate.
  // Absent when no source qualified or the run predates the field.
  hts_sources?: Record<string, number> | null;
  // Pedal-stroke pulse demodulation summary (StrokePulseProcessor).
  // Absent when the analysis was gated off (single magnet, no BLE
  // per-pass series, no crank/power data) or the run predates the field.
  stroke_pulse?: {
    windows_total?: number;
    windows_used?: number;
    windows_degenerate?: number;
    amp_fitted_rps?: number | null;
    amp_predicted_rps?: number | null;
    amp_ratio?: number | null;
    phase_deg?: number | null;
    phase_spread_deg?: number | null;
    snr_median?: number | null;
    detected?: boolean;
    gear_ratio_median?: number | null;
    degeneracy_flagged?: boolean;
    skip_reason?: string | null;
    phase_reference?: string | null;
    power_distribution?: string | null;
    power_distribution_fraction?: number | null;
    mass_phase_shift_deg?: number | null;
    gate_error_max_j?: number | null;
    gate_error_median_j?: number | null;
    gate_error_cda_frac_max?: number | null;
    gate_work_delta_j?: (number | null)[] | null;
    gate_kinetic_delta_j?: (number | null)[] | null;
    gate_correction_applied?: boolean | null;
  } | null;
  whl_route_mapping?: WhlRouteMappingHealth | null;
  traverse_gate_finder?: TraverseGateFinderHealth | null;
  cda_output?: CdaOutputHealth | null;
  cp_analysis?: CpAnalysisHealth | null;
  traverse_structure?: TraverseStructureHealth | null;
}

// ── Weather data health ──

export type WeatherStatus = 'good' | 'fallback' | 'failed';

export interface WeatherSourceSelection {
  channel: string;
  requested_source: string;
  actual_source: string | null;
  fallback_reason?: string | null;
  fallback_chain_tried?: string[];
}

export interface WeatherHealth {
  status: WeatherStatus;
  sources_used: WeatherSourceSelection[];
}

// ── Parser variable overrides ──

export interface CatesbyParserVars {
  speed_threshold?: number | null;
  distance_offset?: number | null;
  // Added to distance_offset on the first traverse only (lap 1); default 100 m
  first_traverse_offset?: number | null;
  stability_window?: number | null;
  acceleration_smoothing_s?: number | null;
  lap_length?: number | null;
  min_duration?: number | null;
  return_distance_offset?: number | null;
  return_lap_length?: number | null;
}

export interface TraverseParserVars {
  min_valid_points?: number | null;
  route_radius?: number | null; // route-averaging merge radius (m); default 40 (was max_radius)
  combine_gaps?: number | null;
  min_distance?: number | null;
  // Wheel→route regression filter (map_whl_to_route)
  wheel_circumference?: number | null; // band centre; defaults to config wheel circ
  whlcrc_deviation?: number | null; // band half-width (m); default 0.3
  min_route_length_n?: number | null; // min revs per lap; default 15
  cp_bearing_cone?: number | null; // Cp direction-split cone (deg); default 2.5
  // Per-traverse acceptance/quality gating (TraverseGateFinder output)
  gate_radius?: number | null; // gate-intersection radius (m); default 50
  require_both_gates?: boolean | null; // default true; exclude traverses missing a gate
  distance_dev_frac?: number | null; // default 0.10; WARN if distance >frac from run median
  bearing_cone_deg?: number | null; // default 45; ± heading-cone half-angle
  bearing_cone_min_frac?: number | null; // default 0.75; WARN if <frac of bearings in cone
}

export interface AtmosphereParserVars {
  // Tri-state: null/absent → inherit (session ← default off); true → replace
  // measured DynPres channels with the synthesized DynPresSynth_pG31 even when
  // probe DP is present; false → explicit run-level "don't force".
  force_synthetic_dp?: boolean | null;
}

export interface ParserVars {
  version: number;
  catesby?: CatesbyParserVars | null;
  velodrome?: Record<string, unknown> | null;
  traverse?: TraverseParserVars | null;
  atmosphere?: AtmosphereParserVars | null;
}

// GET /parser-defaults — the fallback value of every parser_vars field, keyed
// by sub-object. null means "no fixed default, resolved elsewhere at solve
// time" (e.g. traverse.wheel_circumference takes the configured wheel).
export type ParserDefaults = Record<
  string, Record<string, number | boolean | null>
>;

// ── Sensor offset overrides ──

export interface WindSensor5ZeroOverride {
  diff1_zero_pa?: number | null;
  diff2_zero_pa?: number | null;
  diff3_zero_pa?: number | null;
  override_ts?: number | null;
  source?: string | null;
}

export interface SensorOffsets {
  version: number;
  wind_sensor_5?: WindSensor5ZeroOverride | null;
}

export type SensorOffsetsScope = 'this' | 'after' | 'before' | 'all';

// ── Reprocess progress ──

/**
 * Shape returned by `GET /runs/<id>/progress` — a faithful 1:1 of the
 * lightweight backend endpoint (run_progress_controller.py). Used to drive
 * per-run reprocess indicators while a reprocess is in flight.
 *
 * Note: `processing_stage` is a free-text pipeline stage NAME (e.g. 'QUEUED',
 * 'STARTED', 'Grid'), not a numeric code — use `humanizeStage()` to display it.
 */
export interface RunProgress {
  id: string;
  run_status: number;
  processing_stage: string | null;
  processing_started_at: string | null;
  edit_ts: string | null;
}

export interface Run {
  id: string;
  run_ts: string;
  run_status: number;
  description: string;
  hidden: boolean;
  deleted: boolean;
  run_number: number;
  timezone?: string | null;
  average: Record<string, number[]>;
  deltas: Record<string, number[]>;
  traverse: Record<string, number[]>;
  yaw_offset?: number;
  solve?: SolveData | null;
  // Mean gate-averaged air density (kg/m³) the CdA calc used (server
  // computed field; survives the summary wire exclude).
  air_density_used?: number | null;
  parser_vars?: ParserVars | null;
  sensor_offsets?: SensorOffsets | null;
  // The intent/actual split records + the id-keyed fit ledger. (The old
  // run-level ground_speed_setup column is dropped — read these via
  // `runWheelSetup`; patches for sensor-failure repairs go here too.)
  sensors_intent?: SensorsIntent | null;
  sensors_actual?: SensorsActual | null;
  magnet_calc?: RunMagnetCalc | null;
  lap_markers?: LapMarkers | null;
  run_health?: number | null;
  run_health_obj?: RunHealthObj | null;
  run_health_reasons?: string[] | null;
  health_code?: number | null;
  // Reprocess progress columns (present when the run query scope includes them)
  processing_stage?: string | null;
  processing_started_at?: string | null;
  // Nimbus blob-source linkage: set when the run was reassembled from
  // episode blob chunks.
  source_episode_id?: string | null;
  source_run_id?: string | null;
  // Reprocess source, set from the session's "Reprocess Source" option:
  // reprocess rebuilds the zip from the blob chunks first.
  reprocess_from_chunks?: boolean;
  // Upload source: the run's stored zip is the chunk-built artifact rather
  // than a legacy device zip.
  chunk_artifact?: boolean;
  // Chunk-sourced run whose end was never cleanly transmitted — the end
  // time is inferred from the last received data (retroactively terminated).
  partial?: boolean;
  // SQL-computed on the grid scopes: data_gridded_hts IS NOT NULL. Gates
  // the High Rate tab without fetching the HTS payload.
  has_hts?: boolean | null;
}
